import React, { useEffect, useState } from 'react';
import { Users, Plus, Pencil, Trash2, Save, X, Search, Check, ArrowLeft, LogIn, Mail } from 'lucide-react';
import { useApi, useUserContext } from './DashboardLayout';

export default function ManagersPage() {
  const { apiFetch } = useApi();
  const { userType } = useUserContext();
  const [managers, setManagers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(null); // null | 'new' | manager_id
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '', managed_providers: [] });
  const [provSearch, setProvSearch] = useState('');
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('error');
  const [sending, setSending] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [mR, pR] = await Promise.all([
        apiFetch('/api/managers'),
        apiFetch('/api/providers'),
      ]);
      if (mR) {
        const md = await mR.json();
        if (Array.isArray(md)) setManagers(md);
      }
      if (pR) {
        const pd = await pR.json();
        const items = Array.isArray(pd) ? pd : pd?.providers || [];
        setProviders(items);
      }
    } catch (e) {
      console.error('Fehler beim Laden der Verwalter-Daten:', e);
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setForm({ first_name: '', last_name: '', email: '', password: '', managed_providers: [] });
    setEditMode('new');
    setMsg('');
  };

  const startEdit = (m) => {
    setForm({
      first_name: m.first_name || '', last_name: m.last_name || '',
      email: m.email || '', password: '',
      managed_providers: m.managed_providers || [],
    });
    setEditMode(m.id);
    setMsg('');
  };

  const cancel = () => { setEditMode(null); setMsg(''); setProvSearch(''); };

  const toggleProvider = (id) => {
    setForm(prev => ({
      ...prev,
      managed_providers: prev.managed_providers.includes(id)
        ? prev.managed_providers.filter(p => p !== id)
        : [...prev.managed_providers, id]
    }));
  };

  const save = async () => {
    setMsg('');
    const payload = { ...form };
    if (!payload.password) delete payload.password;

    if (editMode === 'new') {
      if (!payload.email || !payload.password) { setMsg('E-Mail und Passwort sind erforderlich'); setMsgType('error'); return; }
      const r = await apiFetch('/api/managers', { method: 'POST', body: JSON.stringify(payload) });
      const d = await r?.json();
      if (r?.ok) { cancel(); loadData(); } else { setMsg(d?.detail || 'Fehler'); setMsgType('error'); }
    } else {
      const r = await apiFetch(`/api/managers/${editMode}`, { method: 'PUT', body: JSON.stringify(payload) });
      const d = await r?.json();
      if (r?.ok) { cancel(); loadData(); } else { setMsg(d?.detail || 'Fehler'); setMsgType('error'); }
    }
  };

  const deleteManager = async (m) => {
    if (!window.confirm(`Verwalter "${m.first_name} ${m.last_name}" wirklich löschen?`)) return;
    await apiFetch(`/api/managers/${m.id}`, { method: 'DELETE' });
    loadData();
  };

  const impersonateManager = async (managerId) => {
    const m = managers.find(x => x.id === managerId);
    if (!m) return;
    if (!window.confirm(`Als "${m.first_name} ${m.last_name}" anmelden?`)) return;
    const r = await apiFetch(`/api/auth/impersonate/${m.id}`, { method: 'POST' });
    const d = await r?.json();
    if (d?.token) {
      localStorage.setItem('bgpr_original_token', localStorage.getItem('bgpr_token'));
      localStorage.setItem('bgpr_original_user_type', localStorage.getItem('bgpr_user_type'));
      localStorage.setItem('bgpr_original_email', localStorage.getItem('bgpr_email'));
      localStorage.setItem('bgpr_impersonating', d.original_admin_email);
      localStorage.setItem('bgpr_token', d.token);
      localStorage.setItem('bgpr_user_type', d.user_type);
      localStorage.setItem('bgpr_email', d.email || '');
      localStorage.setItem('bgpr_user_id', d.user_id);
      window.location.href = '/app';
    }
  };

  const sendCredentialsMail = async (managerId) => {
    const m = managers.find(x => x.id === managerId);
    if (!m) return;
    if (!window.confirm(`Zugangsdaten-Mail an "${m.email}" senden?`)) return;
    setSending(true);
    setMsg('');
    const r = await apiFetch(`/api/managers/${m.id}/send-credentials`, { method: 'POST' });
    const d = await r?.json();
    setSending(false);
    if (r?.ok) {
      setMsg(d?.message || 'Mail gesendet');
      setMsgType('success');
    } else {
      setMsg(d?.detail || 'Fehler beim Senden');
      setMsgType('error');
    }
  };

  const filteredProviders = providers.filter(p =>
    !provSearch || (p.verein || '').toLowerCase().includes(provSearch.toLowerCase()) ||
    (p.club_id || '').includes(provSearch)
  );

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>;

  return (
    <div data-testid="managers-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Verwalter</h2>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>Standort-übergreifende Zugänge</p>
        </div>
        {editMode === null && (
          <button className="btn btn-primary" onClick={startNew} data-testid="new-manager-btn" style={{ fontSize: 13 }}>
            <Plus size={14} /> Neuer Verwalter
          </button>
        )}
      </div>

      {msg && (
        <div className={`design-feedback ${msgType === 'success' ? 'success' : 'error'}`} style={{ marginBottom: 12 }}>
          {msg}
        </div>
      )}

      {/* Edit/Create form */}
      {editMode !== null && (
        <div className="of-card" style={{ marginBottom: 16, padding: 20 }} data-testid="manager-form">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <button className="btn btn-secondary btn-sm" onClick={cancel} data-testid="back-to-managers-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
              <ArrowLeft size={14} /> Zurück zur Liste
            </button>
            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, flex: 1 }}>
              {editMode === 'new' ? 'Neuer Verwalter' : 'Verwalter bearbeiten'}
            </h4>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px', marginBottom: 16 }}>
            <div>
              <label className="of-label">Vorname</label>
              <input className="of-input" value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))}
                data-testid="manager-first-name" />
            </div>
            <div>
              <label className="of-label">Nachname</label>
              <input className="of-input" value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))}
                data-testid="manager-last-name" />
            </div>
            <div>
              <label className="of-label">E-Mail</label>
              <input className="of-input" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                data-testid="manager-email" />
            </div>
            <div>
              <label className="of-label">{editMode === 'new' ? 'Passwort' : 'Neues Passwort (leer = unverändert)'}</label>
              <input className="of-input" type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                placeholder={editMode === 'new' ? '' : 'Leer lassen = unverändert'} data-testid="manager-password" />
            </div>
          </div>

          {/* Provider multi-select */}
          <div style={{ marginBottom: 16 }}>
            <label className="of-label">Zugewiesene Anbieter ({form.managed_providers.length})</label>
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input className="of-input" value={provSearch} onChange={e => setProvSearch(e.target.value)}
                placeholder="Anbieter suchen..." style={{ paddingLeft: 30, fontSize: 12 }}
                data-testid="manager-provider-search" />
            </div>
            <div style={{ maxHeight: 250, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
              {filteredProviders.map(p => {
                const selected = form.managed_providers.includes(p.id);
                return (
                  <div key={p.id} onClick={() => toggleProvider(p.id)}
                    data-testid={`provider-option-${p.id}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer',
                      borderBottom: '1px solid var(--border)', fontSize: 13,
                      background: selected ? 'var(--primary-alpha, rgba(204,0,0,0.05))' : 'transparent',
                    }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: 4, border: selected ? 'none' : '1.5px solid #d1d5db',
                      background: selected ? 'var(--primary)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {selected && <Check size={12} style={{ color: '#fff' }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: selected ? 600 : 400 }}>{p.verein || '-'}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>({p.club_id})</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-sm" onClick={save} data-testid="save-manager-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Save size={14} /> Speichern</button>
            <button className="btn btn-secondary btn-sm" onClick={cancel}
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}><X size={14} /> Abbrechen</button>

            {/* Impersonate & Mail buttons - only in edit mode (not new) */}
            {editMode !== 'new' && (
              <>
                <div style={{ borderLeft: '1px solid var(--border)', height: 28, alignSelf: 'center', margin: '0 4px' }} />
                {(userType === 1 || userType === 2) && (
                  <button className="btn btn-secondary btn-sm" onClick={() => impersonateManager(editMode)}
                    data-testid={`impersonate-manager-${editMode}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <LogIn size={14} /> Als Verwalter anmelden
                  </button>
                )}
                <button className="btn btn-secondary btn-sm" onClick={() => sendCredentialsMail(editMode)}
                  disabled={sending} data-testid={`send-mail-${editMode}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Mail size={14} /> {sending ? 'Wird gesendet...' : 'Zugangsdaten senden'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Manager list */}
      {managers.length === 0 && editMode === null ? (
        <div className="of-card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          <Users size={36} style={{ opacity: 0.2, marginBottom: 8 }} />
          <p style={{ margin: 0, fontSize: 13 }}>Noch keine Verwalter angelegt</p>
        </div>
      ) : editMode === null && (
        <div className="of-card" style={{ padding: 0 }}>
          <table style={{ width: '100%', fontSize: 13 }} data-testid="managers-table">
            <thead>
              <tr style={{ background: 'var(--bg-secondary, #f9fafb)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '10px 16px', textAlign: 'left' }}>E-Mail</th>
                <th style={{ padding: '10px 16px', textAlign: 'left' }}>Zugewiesene Anbieter</th>
                <th style={{ padding: '10px 8px', width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {managers.map(m => (
                <tr key={m.id}
                  onClick={() => startEdit(m)}
                  style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-secondary, #f9fafb)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  data-testid={`manager-row-${m.id}`}>
                  <td style={{ padding: '10px 16px', fontWeight: 600 }}>{m.first_name} {m.last_name}</td>
                  <td style={{ padding: '10px 16px' }}>{m.email}</td>
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(m.managed_provider_names || []).map((name, i) => (
                        <span key={i} style={{ fontSize: 11, background: 'var(--bg-secondary, #f3f4f6)', padding: '2px 8px', borderRadius: 10 }}>{name}</span>
                      ))}
                      {(!m.managed_provider_names || m.managed_provider_names.length === 0) && (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Keine zugewiesen</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button className="btn-icon" onClick={() => startEdit(m)} title="Bearbeiten"
                        data-testid={`edit-manager-${m.id}`}><Pencil size={14} /></button>
                      <button className="btn-icon" onClick={() => deleteManager(m)} title="Löschen"
                        style={{ color: '#EF4444' }} data-testid={`delete-manager-${m.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
